if (!customElements.get('td-freeswatch-submit')) {
  // Serial queue — prevents concurrent cart operations that cause races,
  // cascading fetches, and page-unresponsive errors.
  var _queue = [];
  var _processing = false;

  function enqueueCartOp(fn) {
    return new Promise(function (resolve, reject) {
      _queue.push({ fn: fn, resolve: resolve, reject: reject });
      if (!_processing) drainQueue();
    });
  }

  function drainQueue() {
    if (_processing || !_queue.length) return;
    _processing = true;
    var item = _queue.shift();
    item.fn()
      .then(function (result) { item.resolve(result); })
      .catch(function (err) { item.reject(err); })
      .finally(function () {
        _processing = false;
        drainQueue();
      });
  }

  class TdFreeswatchSubmit extends HTMLElement {
    constructor() {
      super();
      this.checkboxes = this.querySelectorAll('input[type="checkbox"][data-variant-id]');
      this._onCartUpdated = this.handleCartUpdate.bind(this);
      this._onCartRefreshed = this.handleCartRefresh.bind(this);
    }

    connectedCallback() {
      this.checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', (e) => {
          this.handleCheckboxChange(e.target);
        });
      });
      this.syncCheckboxesWithCart();
      document.addEventListener('td-cart-updated', this._onCartUpdated);
      document.addEventListener('td-cart-refreshed', this._onCartRefreshed);
    }

    disconnectedCallback() {
      document.removeEventListener('td-cart-updated', this._onCartUpdated);
      document.removeEventListener('td-cart-refreshed', this._onCartRefreshed);
    }

    handleCartRefresh(event) {
      // Debounce: if another refresh comes in within 300ms, cancel the previous
      clearTimeout(this._refreshTimer);
      this._refreshTimer = setTimeout(() => {
        this.syncCheckboxesWithCart();
      }, 300);
    }

    handleCartUpdate(event) {
      // Skip our own events — the queued operation already handles its state
      if (event.detail && event.detail._src === this) return;

      if (event.detail && event.detail.cart) {
        this.syncCheckboxesWithCartData(event.detail.cart);
      } else {
        this.syncCheckboxesWithCart();
      }
    }

    updateKeyMap(cart) {
      this._keyMap = {};
      cart.items.forEach(item => {
        if (item.properties && item.properties['_Swatch Bundle'] === 'true') {
          this._keyMap[item.variant_id] = item.key;
        }
      });
    }

    syncCheckboxesWithCartData(cart) {
      const cartVariantIds = cart.items
        .filter(item =>
          item.properties &&
          item.properties['_Swatch Bundle'] === 'true' &&
          item.quantity > 0
        )
        .map(item => item.variant_id);

      this.updateKeyMap(cart);

      this.checkboxes.forEach(checkbox => {
        const variantId = parseInt(checkbox.getAttribute('data-variant-id'));
        const isInCart = variantId && cartVariantIds.includes(variantId);
        const isCurrentlyChecked = checkbox.checked;
        const isLoading = checkbox.getAttribute('data-loading') === 'true';

        if (!isLoading && isCurrentlyChecked !== isInCart) {
          checkbox.checked = isInCart;
          this.updateFabricActiveState(checkbox, isInCart);
        }
      });
      this.emitSwatchVisibility();
    }

    syncCheckboxesWithCart() {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          this.syncCheckboxesWithCartData(cart);
        })
        .catch(error => {
          console.error('Error syncing checkboxes with cart:', error);
        });
    }

    updateFabricActiveState(checkbox, isActive) {
      const fabricBlock = checkbox.closest('.freeswatch-fabric-block');
      if (fabricBlock) {
        if (isActive) {
          fabricBlock.classList.add('td-fabric-active');
        } else {
          fabricBlock.classList.remove('td-fabric-active');
        }
      }
    }

    handleCheckboxChange(checkbox) {
      const variantId = parseInt(checkbox.getAttribute('data-variant-id'));

      if (!variantId) {
        console.error('No variant ID found for checkbox');
        return;
      }

      if (checkbox.checked) {
        this.addItemToCart(variantId, checkbox);
      } else {
        this.removeItemFromCart(variantId, checkbox);
      }
    }

    addItemToCart(variantId, checkbox) {
      this.setCheckboxLoading(checkbox, true);

      const item = {
        id: variantId,
        quantity: 1,
        properties: {
          '_Swatch Bundle': 'true'
        }
      };

      const self = this;

      enqueueCartOp(function () {
        // No pre-fetch duplicate check — checkbox state is synced on load.
        return fetch('/cart/add.js?sections=th-cart-drawer,cart-icon-bubble', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ items: [item] })
        })
        .then(response => response.json())
        .then(data => {
          return fetch('/cart.js').then(response => response.json()).then(cart => {
            return { cart: cart, sections: data.sections };
          });
        })
        .then(result => {
          // Update key map from the fresh cart so removes stay fast
          self.updateKeyMap(result.cart);

          document.dispatchEvent(new CustomEvent('td-cart-updated', {
            detail: {
              cart: result.cart,
              sections: result.sections,
              silent: true,
              _src: self
            }
          }));

          self.clearError();
          self.setCheckboxLoading(checkbox, false);
        })
        .catch(error => {
          console.error('Error adding item to cart:', error);
          self.showError('Failed to add item to cart. Please try again.');
          checkbox.checked = false;
          self.setCheckboxLoading(checkbox, false);
        });
      });
    }

    removeItemFromCart(variantId, checkbox) {
      this.setCheckboxLoading(checkbox, true);

      const self = this;

      enqueueCartOp(function () {
        const changeUrl = (window.routes || routes)?.cart_change_url || '/cart/change.js';
        // Use cached line-item key from last sync, fall back to variant ID
        const itemId = (self._keyMap && self._keyMap[variantId]) || variantId;

        const sections = ['th-cart-drawer', 'cart-icon-bubble'];

        const fetchConfig = () => ({
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
        });

        return fetch(changeUrl, {
          ...fetchConfig(),
          body: JSON.stringify({
            id: itemId,
            quantity: 0,
            sections: sections,
            sections_url: window.location.pathname
          })
        })
        .then(response => {
          if (!response.ok) {
            return response.text().then(text => {
              throw new Error('Cart change failed: ' + response.status);
            });
          }
          return response.text();
        })
        .then(state => {
          const parsedState = JSON.parse(state);

          if (parsedState.errors) {
            throw new Error(parsedState.errors);
          }

          // /cart/change.js returns the full cart — no extra GET needed
          self.updateKeyMap(parsedState);

          document.dispatchEvent(new CustomEvent('td-cart-updated', {
            detail: {
              cart: parsedState,
              sections: parsedState.sections || {},
              silent: true,
              _src: self
            }
          }));
          self.clearError();
          self.setCheckboxLoading(checkbox, false);
        })
        .catch(error => {
          console.error('Error removing item from cart:', error);
          self.showError('Failed to remove item from cart. Please try again.');
          checkbox.checked = true;
          self.setCheckboxLoading(checkbox, false);
        });
      });
    }

    setCheckboxLoading(checkbox, isLoading) {
      checkbox.disabled = isLoading;
      checkbox.setAttribute('data-loading', isLoading ? 'true' : 'false');

      const fabricBlock = checkbox.closest('.freeswatch-fabric-block');
      if (fabricBlock) {
        if (isLoading) {
          fabricBlock.classList.add('td-fabric-loading');
          this.classList.add('td-fabrics-processing');
        } else {
          fabricBlock.classList.remove('td-fabric-loading');
          if (!this.querySelector('.td-fabric-loading')) {
            this.classList.remove('td-fabrics-processing');
          }
          this.updateFabricActiveState(checkbox, checkbox.checked);
          this.emitSwatchVisibility();
        }
      }
    }

    emitSwatchVisibility() {
      const hasSwatches = Array.from(this.checkboxes).some(cb => cb.checked);
      document.dispatchEvent(new CustomEvent('td-swatch-visibility', {
        detail: { visible: hasSwatches }
      }));
    }

    showError(message) {
      const errorElement = this.querySelector('.submit-error-msg');
      if (errorElement) {
        errorElement.textContent = message;
        errorElement.style.display = 'block';
      }
    }

    clearError() {
      const errorElement = this.querySelector('.submit-error-msg');
      if (errorElement) {
        errorElement.style.display = 'none';
      }
    }
  }

  customElements.define('td-freeswatch-submit', TdFreeswatchSubmit);
}
